import { useState } from "react";
import { useCSVReader } from "react-papaparse";
import { parseDepartmentBuilderCsv } from "./departmentBuilderCsv.js";
import AccountTreePreview from "./AccountTreePreview.jsx";
import { delay } from "../../utils/commonUtils";
import { fetchGetApiRequestJobStatus, submitDepartmentBatchJob } from "../../utils/apiUtils.js";
import "./DepartmentBuilder.scss";

/** Parallel department SOAP submits per batch (hrp-adapter clamps 1–20). */
const DEPARTMENT_BATCH_CONCURRENCY = 5;

/**
 * Omit `endDate` when absent so open-ended assignments do not send `endDate: null` to the API.
 * @param {Array<Record<string, unknown>>} assignments
 * @returns {Array<Record<string, unknown>>}
 */
const assignmentsForSubmit = (assignments) =>
    assignments.map((a) => {
        const { endDate, ...rest } = a;
        if (endDate != null && String(endDate).trim() !== "") {
            return { ...rest, endDate };
        }
        return rest;
    });

/**
 * `/config/department-builder` page: CSV upload for account/department rows, validation messages,
 * and a tree preview. Submitting data to HRP enqueues an hrp-adapter job (`submit-batch-job`)
 * and polls job status via `hrp-proxy` (same pattern as benefit plan submit-job).
 * @returns {import("react").ReactElement}
 */
const DepartmentBuilder = () => {
    /** Nested account + department strings; sole source for the tree preview. */
    const [accountTree, setAccountTree] = useState(null);
    /** When parse succeeds (`ok`), the list of department assignments to POST (same as `data.assignments`). */
    const [submissionPayload, setSubmissionPayload] = useState(null);
    const [parseErrors, setParseErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    /**
     * @type {{
     *   message: string;
     *   failures?: Array<{ index: number; assignment: Record<string, unknown>; error: string }>;
     *   sent?: unknown;
     *   cause?: unknown;
     * } | null}
     */
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccessCount, setSubmitSuccessCount] = useState(null);
    const { CSVReader } = useCSVReader();

    const canSubmit = Boolean(submissionPayload?.length && accountTree);

    const resetSubmitFeedback = () => {
        setSubmitError(null);
        setSubmitSuccessCount(null);
    };

    const handleSubmitAssignments = async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);
        resetSubmitFeedback();

        try {
            const jobKeys = await submitDepartmentBatchJob({
                body: {
                    assignments: assignmentsForSubmit(submissionPayload),
                    concurrency: DEPARTMENT_BATCH_CONCURRENCY,
                },
            });
            if (!jobKeys?.pk || !jobKeys?.sk) {
                throw new Error("Invalid job response from server");
            }

            let jobStatusRes;
            do {
                await delay(3000);
                jobStatusRes = await fetchGetApiRequestJobStatus({
                    pk: jobKeys.pk,
                    sk: jobKeys.sk,
                });
            } while (jobStatusRes.requestStatus === "PENDING");

            if (jobStatusRes.requestStatus === "ERROR") {
                const errBody = jobStatusRes.errorResponseBody;
                const msg =
                    typeof errBody === "string"
                        ? errBody
                        : errBody?.message ?? "Department batch job failed";
                throw new Error(msg, { cause: errBody });
            }

            let result = jobStatusRes.responseBody;
            if (typeof result === "string") {
                try {
                    result = JSON.parse(result);
                } catch {
                    throw new Error("handleSubmitAssignments: Could not parse job result", { cause: result });
                }
            }

            if (result?.failures?.length) {
                setSubmitError({
                    message:
                        `${result.failures.length} assignment(s) could not be submitted. ` +
                        `${result.changedCount ?? 0} new assignment(s) were created before the failures.`,
                    failures: result.failures,
                });
            } else {
                setSubmitSuccessCount(result?.changedCount ?? 0);
            }
        } catch (e) {
            const err = e instanceof Error ? e : new Error(String(e));
            const rawCause = err.cause;
            const causeObj =
                rawCause && typeof rawCause === "object" && !Array.isArray(rawCause)
                    ? rawCause
                    : null;
            const nestedFailures = causeObj?.failures;

            if (Array.isArray(nestedFailures) && nestedFailures.length > 0) {
                setSubmitError({
                    message: err.message || "Request failed",
                    failures: nestedFailures,
                    cause: causeObj,
                });
            } else {
                setSubmitError({
                    message: err.message || "Request failed",
                    sent: submissionPayload,
                    cause:
                        causeObj ??
                        (typeof rawCause === "string" || typeof rawCause === "number"
                            ? rawCause
                            : undefined),
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="department-builder container scrollable-form">
            <p className="title">Department Builder</p>
            <p className="sub-title">
                Upload a CSV describing accounts (up to three levels: top account, sub-accounts, and
                sub-sub-accounts) and departments assigned to each account. You may include a top-level
                row with an empty Parent Account HCC ID, or omit that row if the top account exists only
                in HRP: when every row has a parent, the parser infers a single top from the one Parent
                HCC ID that never appears as Account HCC ID on any row. If you include a top-level row,
                it must be the first data row. Department names must be unique within each account. End
                Date may be left blank or the column omitted for open-ended assignments.
            </p>

            <p className="department-builder__expected-columns">
                Expected columns:{" "}
                <strong>
                    Account Name, Account HCC ID, Parent Account HCC ID, Department Name, Start Date
                </strong>
                , and optionally <strong>End Date</strong> (omit the column or leave cells empty when
                there is no end date).
            </p>

            <CSVReader
                config={{
                    header: true,
                    skipEmptyLines: true,
                }}
                onUploadAccepted={(results) => {
                    const { ok, errors, data } = parseDepartmentBuilderCsv(results.data);

                    setParseErrors(errors);
                    resetSubmitFeedback();
                    
                    if (data) {
                        setAccountTree(data.accountTree);
                        setSubmissionPayload(ok ? data.assignments : null);
                    } else {
                        setAccountTree(null);
                        setSubmissionPayload(null);
                    }
                }}
            >
                {({ getRootProps, ProgressBar }) => (
                    <div className="department-builder__csv-grid">
                        <div className="department-builder__actions-row">
                            <button
                                className="department-builder__upload-btn"
                                type="button"
                                disabled={isSubmitting}
                                {...getRootProps()}
                            >
                                Upload CSV
                            </button>
                            <button
                                type="button"
                                className="btn edit-btn department-builder__submit-btn"
                                disabled={!canSubmit || isSubmitting}
                                onClick={handleSubmitAssignments}
                            >
                                {isSubmitting ? "Submitting…" : "Submit assignments"}
                            </button>
                        </div>
                        {isSubmitting && (
                            <div className="department-builder__submitting">
                                <div className="loader" />
                                <span className="department-builder__muted">
                                    Submitting assignments to HRP…
                                </span>
                            </div>
                        )}
                        <div className="department-builder__progress-wrap">
                            <ProgressBar />
                        </div>
                    </div>
                )}
            </CSVReader>

            {submitSuccessCount !== null && !submitError && submitSuccessCount === 0 && (
                <div className="department-builder__alert department-builder__alert--warning">
                    <p className="title department-builder__alert-title">No new assignments</p>
                    <p className="department-builder__alert-body">
                        No new department to account assignments were created. It seems they already exist
                        in HRP?
                    </p>
                </div>
            )}

            {submitSuccessCount !== null && !submitError && submitSuccessCount > 0 && (
                <div className="department-builder__alert department-builder__alert--success">
                    <p className="title department-builder__alert-title">Success</p>
                    <p className="department-builder__alert-body-plain">
                        Successfully submitted{" "}
                        <strong>{submitSuccessCount}</strong> new assignment
                        {submitSuccessCount === 1 ? "" : "s"}.
                    </p>
                </div>
            )}

            {submitError && (
                <div className="department-builder__alert department-builder__alert--danger">
                    <p className="title department-builder__alert-title">Submit failed</p>
                    <p className="department-builder__alert-message">{submitError.message}</p>
                    {submitError.cause != null && !submitError.failures?.length && (
                        <pre className="department-builder__pre">
                            {typeof submitError.cause === "string"
                                ? submitError.cause
                                : JSON.stringify(submitError.cause, null, 2)}
                        </pre>
                    )}
                    {submitError.failures && submitError.failures.length > 0 && (
                        <div className="department-builder__failures-wrap">
                            <p className="department-builder__failures-caption">
                                Problem rows (CSV line matches your file — row 1 is the header):
                            </p>
                            <ul className="department-builder__failures-list">
                                {submitError.failures.map((f, i) => (
                                    <li key={`${f.index}-${i}`} className="department-builder__failure-item">
                                        <strong>Row {f.index + 2}</strong>
                                        {": "}
                                        {f.error}
                                        <pre className="department-builder__pre department-builder__pre--compact">
                                            {JSON.stringify(f.assignment, null, 2)}
                                        </pre>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {submitError.sent != null && !submitError.failures?.length && (
                        <>
                            <p className="department-builder__payload-label">Payload sent:</p>
                            <pre className="department-builder__pre department-builder__pre--payload">
                                {JSON.stringify(submitError.sent ?? [], null, 2)}
                            </pre>
                        </>
                    )}
                </div>
            )}

            {parseErrors.length > 0 && (
                <div className="department-builder__alert department-builder__alert--danger department-builder__alert--validation">
                    <p className="title department-builder__alert-title">Validation issues</p>
                    <ul className="department-builder__failures-list">
                        {parseErrors.map((err) => (
                            <li key={err}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}

            {accountTree && (
                <div className="department-builder__tree-section">
                    <p className="title department-builder__tree-heading">Accounts and their departments</p>
                    <AccountTreePreview tree={accountTree} />
                </div>
            )}
        </div>
    );
};

export default DepartmentBuilder;
